import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stockCountsApi, inventoryItemsApi } from '../../api/warehouse';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { STOCK_COUNT_STATUS_LABELS } from './StockCountsListPage';
import { STOCK_COUNT_STAGES } from '../../config/workflow-stages';

export function StockCountWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const countId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: countData, isError } = useQuery({ queryKey: ['stock-count', countId], queryFn: () => stockCountsApi.get(countId) });
  const count = countData?.data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['stock-count', countId] });
  const invalidateItems = () => queryClient.invalidateQueries({ queryKey: ['stock-count-items', countId] });

  const { data: itemsData } = useQuery({ queryKey: ['stock-count-items', countId], queryFn: () => stockCountsApi.listItems(countId) });
  const items = itemsData?.data ?? [];
  const { data: inventoryItemsData } = useQuery({ queryKey: ['inventory-items'], queryFn: () => inventoryItemsApi.list() });
  const inventoryItems = inventoryItemsData?.data ?? [];
  const { data: docsData } = useQuery({ queryKey: ['stock-count-documents', countId], queryFn: () => genericDocumentApi.listFor('stock_count', countId) });

  const [selectedItemId, setSelectedItemId] = useState('');
  const addItemMutation = useMutation({
    mutationFn: () => stockCountsApi.addItem(countId, Number(selectedItemId)),
    onSuccess: () => { showSuccess('Item added for counting.'); setSelectedItemId(''); invalidateItems(); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const [physicalForm, setPhysicalForm] = useState<Record<number, { qty: string; reason: string }>>({});
  const recordMutation = useMutation({
    mutationFn: (lineId: number) => stockCountsApi.recordPhysicalQuantity(lineId, Number(physicalForm[lineId]?.qty), physicalForm[lineId]?.reason || undefined),
    onSuccess: () => { showSuccess('Physical quantity recorded.'); invalidateItems(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const submitMutation = useMutation({ mutationFn: () => stockCountsApi.submit(countId), onSuccess: () => { showSuccess('Count submitted.'); invalidate(); }, onError: (err: ApiError) => showError(err.message) });
  const reviewMutation = useMutation({ mutationFn: () => stockCountsApi.review(countId), onSuccess: () => { showSuccess('Count reviewed.'); invalidate(); }, onError: (err: ApiError) => showError(err.message) });
  const approveMutation = useMutation({
    mutationFn: () => stockCountsApi.approve(countId),
    onSuccess: (result) => { showSuccess(`Count approved - ${result.data.adjustmentsCreated} stock adjustment(s) created for review.`); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const closeMutation = useMutation({ mutationFn: () => stockCountsApi.close(countId), onSuccess: () => { showSuccess('Count closed.'); invalidate(); }, onError: (err: ApiError) => showError(err.message) });

  if (isError) return <div className="page"><div className="error-state">Failed to load this stock count. Please try refreshing the page.</div></div>;
  if (!count) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={count.count_reference}
        readOnly={false}
        entityType="stock_count"
        entityId={countId}
        fields={[
          { label: 'Warehouse', value: count.warehouse_name },
          { label: 'Count Team', value: count.count_team ?? '-' },
          { label: 'Status', value: <StatusBadge status={count.status} label={STOCK_COUNT_STATUS_LABELS[count.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'items', label: 'Count Sheet', content: (
              <div>
                <SectionHeader title="Items" />
                <table className="data-table">
                  <thead><tr><th>Item</th><th>System Qty</th><th>Physical Qty</th><th>Variance</th><th>Reason</th><th></th></tr></thead>
                  <tbody>
                    {items.map((i) => {
                      const variance = i.physical_quantity !== null ? Number(i.physical_quantity) - Number(i.system_quantity) : null;
                      const canEdit = ['planned', 'in_progress'].includes(count.status);
                      return (
                        <tr key={i.id}>
                          <td>{i.item_name}</td>
                          <td>{i.system_quantity} {i.unit_of_measure}</td>
                          <td>{i.physical_quantity ?? (canEdit ? (
                            <input type="number" step="0.01" style={{ width: 90 }} value={physicalForm[i.id]?.qty ?? ''} onChange={(e) => setPhysicalForm((s) => ({ ...s, [i.id]: { ...s[i.id], qty: e.target.value } }))} />
                          ) : '-')}</td>
                          <td>{variance !== null ? variance : '-'}</td>
                          <td>{i.variance_reason ?? (canEdit && !i.physical_quantity ? (
                            <input placeholder="If different from system qty" style={{ width: 160 }} value={physicalForm[i.id]?.reason ?? ''} onChange={(e) => setPhysicalForm((s) => ({ ...s, [i.id]: { ...s[i.id], reason: e.target.value } }))} />
                          ) : '-')}</td>
                          <td>{canEdit && i.physical_quantity === null && (
                            <button onClick={() => recordMutation.mutate(i.id)} disabled={recordMutation.isPending || !physicalForm[i.id]?.qty}>Save</button>
                          )}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {['planned', 'in_progress'].includes(count.status) && (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); addItemMutation.mutate(); }}>
                    <select value={selectedItemId} onChange={(e) => setSelectedItemId(e.target.value)} required>
                      <option value="">Select item to count…</option>
                      {inventoryItems.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </select>
                    <button type="submit" disabled={addItemMutation.isPending}>Add Item</button>
                  </form>
                )}
                {count.status === 'in_progress' && items.length > 0 && (
                  <button onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending || items.some((i) => i.physical_quantity === null)} style={{ marginTop: 12 }}>
                    Submit Count
                  </button>
                )}
              </div>
            ),
          },
          {
            key: 'approval', label: 'Review & Approval', content: (
              <div>
                <SectionHeader title="Review & Approval" />
                {count.status === 'submitted' && <button onClick={() => reviewMutation.mutate()} disabled={reviewMutation.isPending}>Mark Reviewed</button>}
                {count.status === 'reviewed' && (
                  <>
                    <p className="muted">Approving will create a Stock Adjustment for every item with a genuine variance. Each adjustment still requires its own separate approval before stock changes.</p>
                    <button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>Approve Count</button>
                  </>
                )}
                {count.status === 'approved' && <button onClick={() => closeMutation.mutate()} disabled={closeMutation.isPending}>Close Count</button>}
                {count.status === 'closed' && <p className="muted">This count is closed.</p>}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('stock_count', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('stock_count', countId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('stock_count', documentId)}
                queryKeyToInvalidate={['stock-count-documents', countId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="stock_count" entityId={countId} actionStageMap={STOCK_COUNT_STAGES} statusLabels={STOCK_COUNT_STATUS_LABELS} />
                <CommentsThread entityType="stock_count" entityId={countId} section="warehouse" heading="Comments for this Count" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
